import { For, JSX, createSignal, onMount, onCleanup } from "solid-js";
import { useNavigate } from "@solidjs/router";
import Text from "../components/Text";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./Security.module.css";

type SectionDef = { id: string; titleKey: string };

const SECTIONS: SectionDef[] = [
    { id: "intro", titleKey: "SEC_TOC_INTRO" },
    { id: "walkthrough", titleKey: "SEC_TOC_WALKTHROUGH" },
    { id: "cannot", titleKey: "SEC_TOC_CANNOT" },
    { id: "can", titleKey: "SEC_TOC_CAN" },
    { id: "protection", titleKey: "SEC_TOC_PROTECTION" },
    { id: "breach", titleKey: "SEC_TOC_BREACH" },
    { id: "revoke", titleKey: "SEC_TOC_REVOKE" },
    { id: "compare", titleKey: "SEC_TOC_COMPARE" },
    { id: "standards", titleKey: "SEC_TOC_STANDARDS" },
    { id: "threats", titleKey: "SEC_TOC_THREATS" },
    { id: "faq", titleKey: "SEC_TOC_FAQ" },
];

function SectionHeader(props: { id: string; titleKey: string }) {
    return (
        <h2 id={props.id} class={styles.sectionHeading}>
            <a href={`#${props.id}`} class={styles.anchorLink} aria-hidden="true">#</a>
            <Text val={props.titleKey} />
        </h2>
    );
}

function ScreenshotStub(props: { labelKey: string; src?: string }) {
    if (props.src) {
        return (
            <img
                class={styles.screenshotImage}
                src={props.src}
                alt=""
                loading="lazy"
                decoding="async"
            />
        );
    }
    return (
        <div class={styles.screenshotStub} role="img" aria-label={props.labelKey}>
            <i class="fa-solid fa-image" />
            <span><Text val={props.labelKey} /></span>
        </div>
    );
}

function CheckList(props: { items: string[]; variant: "yes" | "no" }) {
    return (
        <ul class={styles.checkList}>
            <For each={props.items}>{(key) => (
                <li class={props.variant === "yes" ? styles.checkYes : styles.checkNo}>
                    <i class={props.variant === "yes" ? "fa-solid fa-check" : "fa-solid fa-xmark"} />
                    <span><Text val={key} /></span>
                </li>
            )}</For>
        </ul>
    );
}

function Collapsible(props: { titleKey: string; bodyKey: string }) {
    const [open, setOpen] = createSignal(false);
    return (
        <div class={styles.collapsible}>
            <button
                type="button"
                class={styles.collapsibleHeader}
                aria-expanded={open()}
                onClick={() => setOpen(v => !v)}
            >
                <i class={`fa-solid ${open() ? "fa-chevron-down" : "fa-chevron-right"}`} />
                <span><Text val={props.titleKey} /></span>
            </button>
            {open() && (
                <div class={styles.collapsibleBody}>
                    <Text val={props.bodyKey} />
                </div>
            )}
        </div>
    );
}

function PermissionRow(props: { ms: string; plain: string }) {
    return (
        <div class={styles.permRow}>
            <div class={styles.permMs}>
                <span class={styles.permMsLabel}><Text val="SEC_PERM_MS_LABEL" /></span>
                <code><Text val={props.ms} /></code>
            </div>
            <div class={styles.permArrow}><i class="fa-solid fa-arrow-right" /></div>
            <div class={styles.permPlain}>
                <span class={styles.permPlainLabel}><Text val="SEC_PERM_PLAIN_LABEL" /></span>
                <Text val={props.plain} />
            </div>
        </div>
    );
}

function ArchitectureDiagram(): JSX.Element {
    const { t } = useTranslation();
    return (
        <svg
            class={styles.diagram}
            viewBox="0 0 800 280"
            role="img"
            aria-labelledby="sec-diagram-title"
            xmlns="http://www.w3.org/2000/svg"
        >
            <title id="sec-diagram-title">{t("SEC_DIAGRAM_TITLE")}</title>
            <defs>
                <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                </marker>
            </defs>
            <g class={styles.diagramNode}>
                <rect x="20" y="100" width="140" height="80" rx="8" />
                <text x="90" y="135" text-anchor="middle">{t("SEC_DIAGRAM_USER")}</text>
                <text x="90" y="155" text-anchor="middle" class={styles.diagramSub}>{t("SEC_DIAGRAM_BROWSER")}</text>
            </g>
            <g class={styles.diagramNode}>
                <rect x="220" y="40" width="180" height="80" rx="8" />
                <text x="310" y="75" text-anchor="middle">{t("SEC_DIAGRAM_MICROSOFT")}</text>
                <text x="310" y="95" text-anchor="middle" class={styles.diagramSub}>{t("SEC_DIAGRAM_MS_SUB")}</text>
            </g>
            <g class={styles.diagramNode}>
                <rect x="220" y="160" width="180" height="80" rx="8" />
                <text x="310" y="195" text-anchor="middle">{t("SEC_DIAGRAM_BACKEND")}</text>
                <text x="310" y="215" text-anchor="middle" class={styles.diagramSub}>{t("SEC_DIAGRAM_AUTH_SERVICE")}</text>
            </g>
            <g class={styles.diagramNode}>
                <rect x="460" y="160" width="180" height="80" rx="8" />
                <text x="550" y="190" text-anchor="middle">{t("SEC_DIAGRAM_TOKEN_STORE")}</text>
                <text x="550" y="210" text-anchor="middle" class={styles.diagramSub}>{t("SEC_DIAGRAM_AES")}</text>
                <text x="550" y="228" text-anchor="middle" class={styles.diagramSub}>{t("SEC_DIAGRAM_ENCRYPTED")}</text>
            </g>
            <g class={styles.diagramNode}>
                <rect x="680" y="40" width="100" height="80" rx="8" />
                <text x="730" y="75" text-anchor="middle">{t("SEC_DIAGRAM_KEY")}</text>
                <text x="730" y="95" text-anchor="middle" class={styles.diagramSub}>{t("SEC_DIAGRAM_KEY_SUB")}</text>
            </g>
            <g class={styles.diagramEdge}>
                <line x1="160" y1="130" x2="220" y2="80" marker-end="url(#arr)" />
                <line x1="160" y1="150" x2="220" y2="200" marker-end="url(#arr)" />
                <line x1="310" y1="120" x2="310" y2="160" marker-end="url(#arr)" />
                <line x1="400" y1="200" x2="460" y2="200" marker-end="url(#arr)" />
                <line x1="730" y1="120" x2="600" y2="170" marker-end="url(#arr)" stroke-dasharray="4 4" />
            </g>
        </svg>
    );
}

export default function Security() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [activeId, setActiveId] = createSignal<string>(SECTIONS[0].id);
    const [tocOpen, setTocOpen] = createSignal(false);

    onMount(() => {
        const hash = window.location.hash.replace("#", "");
        if (hash) {
            queueMicrotask(() => {
                const el = document.getElementById(hash);
                if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
            });
        }
        const handler = () => {
            let current = SECTIONS[0].id;
            for (const s of SECTIONS) {
                const el = document.getElementById(s.id);
                if (!el) continue;
                if (el.getBoundingClientRect().top - 120 <= 0) current = s.id;
            }
            setActiveId(current);
        };
        window.addEventListener("scroll", handler, { passive: true });
        onCleanup(() => window.removeEventListener("scroll", handler));
    });

    const jumpTo = (id: string) => {
        setTocOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        history.replaceState(null, "", `#${id}`);
    };

    return (
        <div class={styles.page}>
            <div class={styles.heroBand}>
                <div class={styles.heroInner}>
                    <span class={styles.eyebrow}><Text val="SEC_EYEBROW" /></span>
                    <h1 class={styles.heroTitle}><Text val="SEC_HERO_TITLE" /></h1>
                    <p class={styles.heroSubtitle}><Text val="SEC_HERO_SUBTITLE" /></p>
                </div>
            </div>

            <div class={styles.layout}>
                <aside class={styles.tocSidebar}>
                    <button
                        type="button"
                        class={styles.tocToggle}
                        onClick={() => setTocOpen(v => !v)}
                        aria-expanded={tocOpen()}
                    >
                        <i class="fa-solid fa-list" />
                        <span><Text val="SEC_TOC_JUMP" /></span>
                    </button>
                    <nav class={`${styles.tocNav} ${tocOpen() ? styles.tocOpen : ""}`} aria-label={t("SEC_TOC_ARIA_LABEL")}>
                        <For each={SECTIONS}>{(s, i) => (
                            <button
                                type="button"
                                class={`${styles.tocItem} ${activeId() === s.id ? styles.tocActive : ""}`}
                                onClick={() => jumpTo(s.id)}
                            >
                                <span class={styles.tocNum}>{String(i() + 1).padStart(2, "0")}</span>
                                <Text val={s.titleKey} />
                            </button>
                        )}</For>
                    </nav>
                </aside>

                <main class={styles.content}>
                    <section class={styles.section}>
                        <SectionHeader id="intro" titleKey="SEC_INTRO_TITLE" />
                        <p><Text val="SEC_INTRO_P1" /></p>
                        <p><Text val="SEC_INTRO_P2" /></p>
                        <h3><Text val="SEC_INTRO_MULTIMC_TITLE" /></h3>
                        <p class={styles.calloutMuted}><Text val="SEC_INTRO_MULTIMC_BODY" /></p>
                        <div class={styles.quickLinks}>
                            <For each={SECTIONS.slice(1)}>{(s) => (
                                <button type="button" class={styles.quickLink} onClick={() => jumpTo(s.id)}>
                                    <Text val={s.titleKey} />
                                    <i class="fa-solid fa-arrow-right" />
                                </button>
                            )}</For>
                        </div>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="walkthrough" titleKey="SEC_WALKTHROUGH_TITLE" />
                        <p><Text val="SEC_WALKTHROUGH_INTRO" /></p>

                        <div class={styles.walkStep}>
                            <div class={styles.walkStepNum}>1</div>
                            <div class={styles.walkStepBody}>
                                <h3><Text val="SEC_WALK_STEP1_TITLE" /></h3>
                                <p><Text val="SEC_WALK_STEP1_DESC" /></p>
                                <ScreenshotStub labelKey="SEC_WALK_STEP1_SHOT" src="/security/oauth-step1-vektor-login.webp" />
                            </div>
                        </div>

                        <div class={styles.walkStep}>
                            <div class={styles.walkStepNum}>2</div>
                            <div class={styles.walkStepBody}>
                                <h3><Text val="SEC_WALK_STEP2_TITLE" /></h3>
                                <p><Text val="SEC_WALK_STEP2_DESC" /></p>
                                <ScreenshotStub labelKey="SEC_WALK_STEP2_SHOT" src="/security/oauth-step2-ms-login.webp" />
                            </div>
                        </div>

                        <div class={styles.walkStep}>
                            <div class={styles.walkStepNum}>3</div>
                            <div class={styles.walkStepBody}>
                                <h3><Text val="SEC_WALK_STEP3_TITLE" /></h3>
                                <p><Text val="SEC_WALK_STEP3_DESC" /></p>
                                <ScreenshotStub labelKey="SEC_WALK_STEP3_SHOT" src="/security/oauth-step3-consent.webp" />
                                <div class={styles.permList}>
                                    <PermissionRow ms="SEC_PERM_XBOX_MS" plain="SEC_PERM_XBOX_PLAIN" />
                                    <PermissionRow ms="SEC_PERM_OFFLINE_MS" plain="SEC_PERM_OFFLINE_PLAIN" />
                                </div>
                            </div>
                        </div>

                        <div class={styles.walkStep}>
                            <div class={styles.walkStepNum}>4</div>
                            <div class={styles.walkStepBody}>
                                <h3><Text val="SEC_WALK_STEP4_TITLE" /></h3>
                                <p><Text val="SEC_WALK_STEP4_DESC" /></p>
                                <ScreenshotStub labelKey="SEC_WALK_STEP4_SHOT" src="/security/oauth-step4-callback.webp" />
                            </div>
                        </div>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="cannot" titleKey="SEC_CANNOT_TITLE" />
                        <p><Text val="SEC_CANNOT_INTRO" /></p>
                        <CheckList variant="no" items={[
                            "SEC_CANNOT_1", "SEC_CANNOT_2", "SEC_CANNOT_3",
                            "SEC_CANNOT_4", "SEC_CANNOT_5", "SEC_CANNOT_6",
                            "SEC_CANNOT_7", "SEC_CANNOT_8",
                        ]} />
                        <p class={styles.calloutMuted}><Text val="SEC_CANNOT_OUTRO" /></p>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="can" titleKey="SEC_CAN_TITLE" />
                        <p><Text val="SEC_CAN_INTRO" /></p>
                        <CheckList variant="yes" items={[
                            "SEC_CAN_1", "SEC_CAN_2", "SEC_CAN_3",
                            "SEC_CAN_4", "SEC_CAN_5", "SEC_CAN_6",
                        ]} />
                        <p class={styles.calloutMuted}><Text val="SEC_CAN_OUTRO" /></p>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="protection" titleKey="SEC_PROTECTION_TITLE" />
                        <ArchitectureDiagram />
                        <ul class={styles.bulletList}>
                            <li><strong><Text val="SEC_PROT_1_TITLE" /></strong> <Text val="SEC_PROT_1_BODY" /></li>
                            <li><strong><Text val="SEC_PROT_2_TITLE" /></strong> <Text val="SEC_PROT_2_BODY" /></li>
                            <li><strong><Text val="SEC_PROT_3_TITLE" /></strong> <Text val="SEC_PROT_3_BODY" /></li>
                            <li><strong><Text val="SEC_PROT_4_TITLE" /></strong> <Text val="SEC_PROT_4_BODY" /></li>
                            <li><strong><Text val="SEC_PROT_6_TITLE" /></strong> <Text val="SEC_PROT_6_BODY" /></li>
                            <li><strong><Text val="SEC_PROT_7_TITLE" /></strong> <Text val="SEC_PROT_7_BODY" /></li>
                        </ul>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="breach" titleKey="SEC_BREACH_TITLE" />
                        <p><Text val="SEC_BREACH_INTRO" /></p>
                        <h3><Text val="SEC_BREACH_PROMISE_TITLE" /></h3>
                        <p><Text val="SEC_BREACH_PROMISE_BODY" /></p>
                        <h3><Text val="SEC_BREACH_DO_TITLE" /></h3>
                        <p><Text val="SEC_BREACH_DO_BODY" /></p>
                        <h3><Text val="SEC_BREACH_SAFE_TITLE" /></h3>
                        <p><Text val="SEC_BREACH_SAFE_BODY" /></p>
                        <h3><Text val="SEC_BREACH_LIMIT_TITLE" /></h3>
                        <p><Text val="SEC_BREACH_LIMIT_BODY" /></p>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="revoke" titleKey="SEC_REVOKE_TITLE" />
                        <p><Text val="SEC_REVOKE_INTRO" /></p>
                        <ol class={styles.numList}>
                            <li>
                                <Text val="SEC_REVOKE_S1" /> <a class={styles.contentLink} href="https://account.live.com/consent/Manage" target="_blank" rel="noopener">account.live.com/consent/Manage</a>
                                <ScreenshotStub labelKey="SEC_REVOKE_S1_SHOT" src="/security/revoke-step1-app-list.webp" />
                            </li>
                            <li>
                                <Text val="SEC_REVOKE_S2" />
                            </li>
                            <li>
                                <Text val="SEC_REVOKE_S3" />
                                <ScreenshotStub labelKey="SEC_REVOKE_S3_SHOT" src="/security/revoke-step3-remove.webp" />
                            </li>
                            <li><Text val="SEC_REVOKE_S4" /></li>
                        </ol>
                        <p class={styles.calloutMuted}><Text val="SEC_REVOKE_OUTRO" /></p>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="compare" titleKey="SEC_COMPARE_TITLE" />
                        <p><Text val="SEC_COMPARE_INTRO" /></p>
                        <div class={styles.tableWrap}>
                            <table class={styles.compareTable}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th><Text val="SEC_COMPARE_COL_CLIENT" /></th>
                                        <th><Text val="SEC_COMPARE_COL_HOSTED" /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><th><Text val="SEC_COMPARE_R1" /></th><td><Text val="SEC_COMPARE_R1_CLIENT" /></td><td><Text val="SEC_COMPARE_R1_HOSTED" /></td></tr>
                                    <tr><th><Text val="SEC_COMPARE_R2" /></th><td><Text val="SEC_COMPARE_R2_CLIENT" /></td><td><Text val="SEC_COMPARE_R2_HOSTED" /></td></tr>
                                    <tr><th><Text val="SEC_COMPARE_R3" /></th><td><Text val="SEC_COMPARE_R3_CLIENT" /></td><td><Text val="SEC_COMPARE_R3_HOSTED" /></td></tr>
                                    <tr><th><Text val="SEC_COMPARE_R4" /></th><td><Text val="SEC_COMPARE_R4_CLIENT" /></td><td><Text val="SEC_COMPARE_R4_HOSTED" /></td></tr>
                                    <tr><th><Text val="SEC_COMPARE_R5" /></th><td><Text val="SEC_COMPARE_R5_CLIENT" /></td><td><Text val="SEC_COMPARE_R5_HOSTED" /></td></tr>
                                    <tr><th><Text val="SEC_COMPARE_R6" /></th><td><Text val="SEC_COMPARE_R6_CLIENT" /></td><td><Text val="SEC_COMPARE_R6_HOSTED" /></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="standards" titleKey="SEC_STANDARDS_TITLE" />
                        <p><Text val="SEC_STANDARDS_INTRO" /></p>
                        <ul class={styles.bulletList}>
                            <li><strong><Text val="SEC_STD_GDPR_TITLE" /></strong> <Text val="SEC_STD_GDPR_BODY" /></li>
                            <li><strong><Text val="SEC_STD_OAUTH_TITLE" /></strong> <Text val="SEC_STD_OAUTH_BODY" /></li>
                            <li><strong><Text val="SEC_STD_AES_TITLE" /></strong> <Text val="SEC_STD_AES_BODY" /></li>
                            <li><strong><Text val="SEC_STD_TLS_TITLE" /></strong> <Text val="SEC_STD_TLS_BODY" /></li>
                        </ul>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="threats" titleKey="SEC_THREATS_TITLE" />
                        <p><Text val="SEC_THREATS_INTRO" /></p>
                        <div class={styles.threatList}>
                            <Collapsible titleKey="SEC_THREAT_1_TITLE" bodyKey="SEC_THREAT_1_BODY" />
                            <Collapsible titleKey="SEC_THREAT_2_TITLE" bodyKey="SEC_THREAT_2_BODY" />
                            <Collapsible titleKey="SEC_THREAT_3_TITLE" bodyKey="SEC_THREAT_3_BODY" />
                            <Collapsible titleKey="SEC_THREAT_4_TITLE" bodyKey="SEC_THREAT_4_BODY" />
                            <Collapsible titleKey="SEC_THREAT_5_TITLE" bodyKey="SEC_THREAT_5_BODY" />
                            <Collapsible titleKey="SEC_THREAT_6_TITLE" bodyKey="SEC_THREAT_6_BODY" />
                            <Collapsible titleKey="SEC_THREAT_7_TITLE" bodyKey="SEC_THREAT_7_BODY" />
                        </div>
                        <p class={styles.calloutMuted}><Text val="SEC_THREATS_OUTRO" /></p>
                    </section>

                    <section class={styles.section}>
                        <SectionHeader id="faq" titleKey="SEC_FAQ_TITLE" />
                        <div class={styles.threatList}>
                            <Collapsible titleKey="SEC_FAQ_1_TITLE" bodyKey="SEC_FAQ_1_BODY" />
                            <Collapsible titleKey="SEC_FAQ_2_TITLE" bodyKey="SEC_FAQ_2_BODY" />
                            <Collapsible titleKey="SEC_FAQ_3_TITLE" bodyKey="SEC_FAQ_3_BODY" />
                            <Collapsible titleKey="SEC_FAQ_4_TITLE" bodyKey="SEC_FAQ_4_BODY" />
                            <Collapsible titleKey="SEC_FAQ_5_TITLE" bodyKey="SEC_FAQ_5_BODY" />
                            <Collapsible titleKey="SEC_FAQ_6_TITLE" bodyKey="SEC_FAQ_6_BODY" />
                        </div>
                    </section>

                    <section class={styles.ctaBand}>
                        <h2><Text val="SEC_CTA_TITLE" /></h2>
                        <p><Text val="SEC_CTA_DESC" /></p>
                        <div class={styles.ctaButtons}>
                            <button type="button" class={styles.ctaPrimary} onClick={() => navigate("/register")}>
                                <Text val="SEC_CTA_PRIMARY" />
                            </button>
                            <button type="button" class={styles.ctaSecondary} onClick={() => navigate("/")}>
                                <Text val="SEC_CTA_SECONDARY" />
                            </button>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}
